/**
 * configBackup.js — TAV POS
 * ============================================================
 * Utilidad de respaldo y recuperación de configuración del dispositivo.
 * Estrategia de almacenamiento por capas (de más a menos persistente):
 *
 *  1. OPFS  (Origin Private File System) — Chrome/Edge desktop + Android Chrome 108+, iOS Safari 17+
 *     Archivo real dentro del sandbox del navegador. Sobrevive limpiar caché de sitio
 *     SOLO si el usuario limpia "todos los datos" del navegador.
 *     Ruta física (invisible al usuario):
 *       Windows  → %LOCALAPPDATA%\{Browser}\User Data\Default\File System\Origins\...
 *       Android  → /data/data/com.android.chrome/app_chrome/Default/File System/...
 *       iOS      → contenedor privado de Safari
 *
 *  2. IndexedDB — todos los navegadores modernos
 *     Más persistente que localStorage. Base de datos local del navegador.
 *
 *  3. localStorage — fallback final (ya existente en el sistema)
 *
 *  4. Archivo JSON descargable — respaldo portátil iniciado por el usuario.
 *     El usuario guarda el archivo en donde quiera (Documentos, Drive, etc.)
 *     y puede importarlo en cualquier dispositivo.
 *
 * ============================================================
 * API pública:
 *   saveConfigBackup(config)        → guarda en OPFS + IDB
 *   loadConfigBackup()              → retorna { config, source } o null
 *   clearConfigBackup()             → elimina respaldos de OPFS + IDB
 *   exportConfigFile(config)        → descarga tavpos_config.json
 *   importConfigFile()              → abre selector de archivo, retorna config o null
 * ============================================================
 */

const OPFS_FILENAME  = 'tavpos_device_config.json';
const IDB_DB_NAME    = 'tavposConfigBackup';
const IDB_DB_VERSION = 1;
const IDB_STORE      = 'config';
const IDB_KEY        = 'device_config_backup';
const EXPORT_FILENAME = 'tavpos_config.json';

// ─────────────────────────────────────────────
//  OPFS  (Origin Private File System)
// ─────────────────────────────────────────────

const isOPFSAvailable = () =>
  typeof navigator !== 'undefined' &&
  typeof navigator.storage?.getDirectory === 'function';

const saveToOPFS = async (config) => {
  if (!isOPFSAvailable()) return false;
  try {
    const root = await navigator.storage.getDirectory();
    const fh = await root.getFileHandle(OPFS_FILENAME, { create: true });
    const writable = await fh.createWritable();
    await writable.write(JSON.stringify(config, null, 2));
    await writable.close();
    console.log('[configBackup] OPFS: guardado correctamente');
    return true;
  } catch (err) {
    console.warn('[configBackup] OPFS no disponible o error:', err.message);
    return false;
  }
};

const loadFromOPFS = async () => {
  if (!isOPFSAvailable()) return null;
  try {
    const root = await navigator.storage.getDirectory();
    const fh = await root.getFileHandle(OPFS_FILENAME);
    const file = await fh.getFile();
    const text = await file.text();
    const config = JSON.parse(text);
    console.log('[configBackup] OPFS: configuración encontrada para:', config.Propiedad);
    return config;
  } catch {
    return null;
  }
};

const clearFromOPFS = async () => {
  if (!isOPFSAvailable()) return;
  try {
    const root = await navigator.storage.getDirectory();
    await root.removeEntry(OPFS_FILENAME);
    console.log('[configBackup] OPFS: respaldo eliminado');
  } catch {
    // El archivo puede no existir
  }
};

// ─────────────────────────────────────────────
//  IndexedDB
// ─────────────────────────────────────────────

const openConfigDB = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_DB_NAME, IDB_DB_VERSION);
    req.onupgradeneeded = (e) => {
      e.target.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror  = (e) => reject(e.target.error);
  });

const saveToIDB = async (config) => {
  try {
    const db = await openConfigDB();
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(config, IDB_KEY);
    await new Promise((res, rej) => {
      tx.oncomplete = res;
      tx.onerror    = () => rej(tx.error);
    });
    db.close();
    console.log('[configBackup] IDB: guardado correctamente');
    return true;
  } catch (err) {
    console.warn('[configBackup] IDB error al guardar:', err.message);
    return false;
  }
};

const loadFromIDB = async () => {
  try {
    const db = await openConfigDB();
    const tx = db.transaction(IDB_STORE, 'readonly');
    const config = await new Promise((res, rej) => {
      const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
      req.onsuccess = () => res(req.result);
      req.onerror   = () => rej(req.error);
    });
    db.close();
    if (config) {
      console.log('[configBackup] IDB: configuración encontrada para:', config.Propiedad);
    }
    return config || null;
  } catch (err) {
    console.warn('[configBackup] IDB error al cargar:', err.message);
    return null;
  }
};

const clearFromIDB = async () => {
  try {
    const db = await openConfigDB();
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).delete(IDB_KEY);
    await new Promise((res) => { tx.oncomplete = res; });
    db.close();
    console.log('[configBackup] IDB: respaldo eliminado');
  } catch {
    // Ignorar si no existe
  }
};

// ─────────────────────────────────────────────
//  Exportar archivo JSON (descarga al dispositivo)
// ─────────────────────────────────────────────

export const exportConfigFile = (config) => {
  try {
    const json = JSON.stringify(config, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = EXPORT_FILENAME;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    console.log('[configBackup] Archivo exportado:', EXPORT_FILENAME);
    return true;
  } catch (err) {
    console.error('[configBackup] Error al exportar archivo:', err.message);
    return false;
  }
};

// ─────────────────────────────────────────────
//  Importar archivo JSON (selector de archivo)
// ─────────────────────────────────────────────

export const importConfigFile = () =>
  new Promise((resolve) => {
    const input = document.createElement('input');
    input.type   = 'file';
    input.accept = '.json,application/json';

    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return resolve(null);
      try {
        const text   = await file.text();
        const config = JSON.parse(text);

        // Validación mínima
        if (!config.Propiedad || !config.name || !config.Rol) {
          console.warn('[configBackup] Archivo importado no es una configuración válida');
          resolve(null);
          return;
        }
        console.log('[configBackup] Archivo importado correctamente:', config.Propiedad, config.name);
        resolve(config);
      } catch (err) {
        console.error('[configBackup] Error al leer archivo:', err.message);
        resolve(null);
      }
    };

    input.oncancel = () => resolve(null);
    input.click();
  });

// ─────────────────────────────────────────────
//  API PÚBLICA PRINCIPAL
// ─────────────────────────────────────────────

/**
 * Guarda la configuración del dispositivo en OPFS e IndexedDB.
 * Se llama automáticamente al registrar exitosamente un dispositivo.
 */
export const saveConfigBackup = async (config) => {
  const results = await Promise.allSettled([
    saveToOPFS(config),
    saveToIDB(config),
  ]);
  const opfs = results[0].status === 'fulfilled' && results[0].value;
  const idb  = results[1].status === 'fulfilled' && results[1].value;
  console.log(`[configBackup] Guardado — OPFS: ${opfs}, IDB: ${idb}`);
  return { opfs, idb };
};

/**
 * Busca un respaldo de configuración guardado previamente.
 * Prioridad: OPFS → IndexedDB
 * Retorna { config, source } o null si no hay respaldo.
 */
export const loadConfigBackup = async () => {
  // 1. Intentar OPFS (más persistente en desktop)
  const opfsConfig = await loadFromOPFS();
  if (opfsConfig) return { config: opfsConfig, source: 'opfs' };

  // 2. Intentar IndexedDB
  const idbConfig = await loadFromIDB();
  if (idbConfig) return { config: idbConfig, source: 'idb' };

  // 3. Intentar localStorage como último recurso
  try {
    const raw = localStorage.getItem('currentDeviceConfig');
    if (raw) {
      const config = JSON.parse(raw);
      if (config.Propiedad && config.name) {
        console.log('[configBackup] localStorage: configuración encontrada como último recurso');
        return { config, source: 'localStorage' };
      }
    }
  } catch {
    // Ignorar
  }

  return null;
};

/**
 * Elimina los respaldos almacenados en OPFS e IndexedDB.
 * Se llama cuando el usuario confirma un restablecimiento total.
 */
export const clearConfigBackup = async () => {
  await Promise.allSettled([clearFromOPFS(), clearFromIDB()]);
  console.log('[configBackup] Todos los respaldos eliminados');
};

// ─────────────────────────────────────────────
//  Limpieza TOTAL — borra absolutamente todo
// ─────────────────────────────────────────────

/**
 * Elimina TODO el almacenamiento local del navegador:
 * localStorage, todas las bases IndexedDB, Cache API, OPFS.
 * Se usa antes de window.location.reload() en el reset total.
 */
export const clearAllData = async () => {
  // 1. localStorage
  localStorage.clear();

  // 2. sessionStorage
  sessionStorage.clear();

  // 3. Cookies del sitio actual
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const name = cookie.split('=')[0].trim();
      if (!name) continue;
      // Borrar en la ruta raíz y en la ruta actual
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${window.location.pathname}`;
    }
  } catch { /* Ignorar */ }

  // 4. Todas las bases IndexedDB
  const dbsToDelete = ['POSDatabase', IDB_DB_NAME];
  for (const dbName of dbsToDelete) {
    await new Promise((resolve) => {
      try {
        const req = indexedDB.deleteDatabase(dbName);
        req.onsuccess  = resolve;
        req.onerror    = resolve;
        req.onblocked  = resolve;
      } catch { resolve(); }
    });
  }
  if (indexedDB.databases) {
    try {
      const allDbs = await indexedDB.databases();
      for (const { name } of allDbs) {
        if (name && !dbsToDelete.includes(name)) {
          await new Promise((resolve) => {
            const req = indexedDB.deleteDatabase(name);
            req.onsuccess = resolve;
            req.onerror   = resolve;
          });
        }
      }
    } catch { /* Algunos navegadores no soportan .databases() */ }
  }

  // 5. Cache API (service workers / fetch cache)
  if ('caches' in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    } catch { /* Ignorar */ }
  }

  // 6. Desregistrar Service Workers
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    } catch { /* Ignorar */ }
  }

  // 7. OPFS
  await clearFromOPFS();

  console.log('[configBackup] clearAllData: todo el almacenamiento del sitio limpiado');
};

// ─────────────────────────────────────────────
//  Limpieza de caché temporal (Soft Reset)
//  Solo borra POSDatabase (caché de productos/datos).
//  NO toca tavposConfigBackup ni OPFS.
// ─────────────────────────────────────────────

/**
 * Elimina toda la caché de datos temporales (POSDatabase) sin
 * afectar la configuración del dispositivo ni los respaldos.
 * Claves de localStorage que se limpian: currentUser, token.
 * Se conservan: currentDeviceConfig, currentProperty, language_pref_*.
 */
export const clearCacheData = async () => {
  // 1. Borrar IndexedDB de caché de datos (POSDatabase)
  await new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase('POSDatabase');
      req.onsuccess = () => {
        console.log('[configBackup] POSDatabase eliminada');
        resolve();
      };
      req.onerror = () => {
        console.warn('[configBackup] Error al eliminar POSDatabase');
        resolve(); // Continuar aunque falle
      };
      req.onblocked = () => {
        console.warn('[configBackup] POSDatabase bloqueada, reintentando...');
        resolve();
      };
    } catch (err) {
      console.warn('[configBackup] clearCacheData error:', err.message);
      resolve();
    }
  });

  // 2. Limpiar sesión de localStorage (conservar config del dispositivo)
  const KEEP_KEYS = ['currentDeviceConfig', 'currentProperty'];
  const keysToRemove = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    // Conservar config del dispositivo y preferencias de idioma
    if (!KEEP_KEYS.includes(key) && !key?.startsWith('language_pref_')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(k => localStorage.removeItem(k));

  console.log('[configBackup] Datos temporales limpiados. Claves eliminadas:', keysToRemove);
  return { cleared: keysToRemove };
};

/**
 * Retorna la plataforma detectada: 'windows' | 'android' | 'ios' | 'other'
 * Útil para mostrar instrucciones específicas de cada OS.
 */
export const detectPlatform = () => {
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/win/i.test(navigator.platform || ua)) return 'windows';
  return 'other';
};

/**
 * Retorna si OPFS está disponible en el navegador actual.
 */
export const isOPFSSupported = isOPFSAvailable;
